import React, {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useReducer
} from 'react'
import LOG from './logging/Logger'
import { Footer, Navigation, Main } from './layout'
import Splash from './components/splash/Splash'
import { DragDropContext } from 'react-beautiful-dnd'
import { navigation } from './data/navigation'
import { Hub } from '@aws-amplify/core'
import { Auth } from '@aws-amplify/auth'
import { DataStore } from '@aws-amplify/datastore'
import { Storage } from '@aws-amplify/storage'
import { Area, Attachment, Project, Repeat, Tag, Todo } from './models'
import SimpleCache from './data/Cache'
import { v4 as uuidv4 } from 'uuid'
import { sortByName as doSortByName, sortByPositionHint } from './data/sorting'
import { useLocation } from 'react-router-dom'
import { pathToRegexp } from 'path-to-regexp'
import moment from 'moment'
import RepeatPattern from './data/util/repeat/RepeatPattern'
import { processLargeArrayAsync } from './data/UnitOfWork'
import { FOCUS, isValidList } from './data/focus'
import { ITEM_TYPE } from './data/itemType'
import TodoPositions from './data/util/todo/TodoPositions'
import { NO_AREA } from './data/areas'
import Popup from './components/Popup'
import {
  getAllAttachments,
  getAllTags,
  getAllTodos,
  getAllProjects,
  getAllAreas,
  getAllRepeats,
  observeAreas,
  observeAttachments,
  observeProjects,
  observeRepeats,
  observeTags,
  observeTodos,
  saveLocal,
  getLocal,
  getAllForeignTodos
} from './data/store'
import IdleRefresh from './components/IdleRefresh'
import enforceLimit from './components/EnforceLimit'
import {
  EXPIRY_CHECK_INTERVAL_MS,
  EXPIRY_INTERVAL_COMPLETE_DAYS,
  EXPIRY_INTERVAL_TRASH_DAYS,
  OBSERVER_DEBOUNCE_INTERVAL,
  REPEAT_CHECK_INTERVAL_MS
} from './data/intervals'
import _ from 'lodash'
import ConfirmYesNoView from './components/confirmation/ConfirmYesNoView'
import Typography from '@material-ui/core/Typography'
import { MAX_ATTACHMENT_BYTES, MAX_STORAGE_BYTES } from './data/limits'
import FileSaver from 'file-saver'
import { useMountEffect } from './data/hooks'
import { getVersion } from './data/util/environment'
import Encrytpter from './data/util/Encrypter'

const encryption = new Encrytpter()

const countCache = new SimpleCache()
const todoCache = new SimpleCache()
const projectCache = new SimpleCache()
const areaCache = new SimpleCache()
const repeatCache = new SimpleCache()
const tagCache = new SimpleCache()
const attachmentCache = new SimpleCache()

const encrypt = str => encryption.encrypt(str)
const decrypt = str => encryption.decrypt(str)

const getExportableArea = area => {
  return {
    ...area,
    name: decrypt(area.name)
  }
}

const getExportableProject = project => {
  return {
    ...project,
    name: decrypt(project.name),
    description: decrypt(project.description)
  }
}

const getExportableRepeat = repeat => {
  return {
    ...repeat
  }
}

const getExportableTag = tag => {
  return {
    ...tag
  }
}

const getExportableTodo = todo => {
  return {
    ...todo,
    name: decrypt(todo.name),
    description: decrypt(todo.description)
  }
}

// Unique id for this instance
const CLIENT_ID = uuidv4()

const AREA_FILTER_LS_KEY = 'areaFilter'

const sortByName = array => {
  // Need to move decryption into a separate class
  return doSortByName(array, decrypt)
}

let callingBackendCounter = 0

let isHousekeeping = false
let isTriggeringRepeats = false

function useInterval(callback, delay, fireImmediately) {
  const savedCallback = useRef()

  // Remember the latest function.
  useMountEffect(() => {
    savedCallback.current = callback
  })

  // Set up the interval.
  useMountEffect(() => {
    function tick() {
      savedCallback.current()
    }
    if (fireImmediately) {
      setTimeout(tick, 1000)
    }
    if (delay !== null) {
      const id = setInterval(tick, delay)
      return () => clearInterval(id)
    }
  })
}

const onDragEnd = (editTodoProject, handleTodoDragEnd, result) => {
  const { destination } = result

  // dropped outside the list
  if (!destination) {
    return
  }

  if (destination.droppableId.startsWith('project:')) {
    // Special case, no need to reorder, just assign project
    editTodoProject(result.draggableId, destination.droppableId.substring(8))
    return
  }

  if (destination.droppableId.startsWith('nav:')) {
    // Treat as dragging to the top of the list
    const listTarget = destination.droppableId.substring(4)
    handleTodoDragEnd(result.draggableId, listTarget, 0)
    return
  }

  handleTodoDragEnd(
    result.draggableId,
    destination.droppableId,
    destination.index
  )
}

const initialState = {
  loaded: false,
  syncStatus: null,
  datastoreReady: false,
  isCallingBackend: false,
  isOffline: false,
  isSyncing: false,
  showSubscriptionPlans: false,
  info: null,
  warning: null,
  error: null,
  owner: null,
  areaFilter: [],
  openAreaFilter: false,
  todos: [],
  projects: [],
  areas: [],
  repeats: [],
  tags: [],
  attachments: [],
  attachmentsSize: 0,
  navigationSelectedItem: {},
  cacheDragList: {},
  confirmAutoPromote: null,
  confirmAutoDelete: null
}

const debounceCache = {
  update: {
    areas: [],
    attachments: [],
    projects: [],
    repeats: [],
    tags: [],
    todos: []
  },
  delete: {
    areas: [],
    attachments: [],
    projects: [],
    repeats: [],
    tags: [],
    todos: []
  }
}

const GettingFocusedApp = memo(({ history }) => {
  LOG.debug('Render: GettingFocusedApp')

  const [state, dispatch] = useReducer(reducer, initialState)
  const location = useLocation()

  // Info/Errors
  const setInfo = useCallback(info => {
    dispatch({ type: 'SET_INFO', info })
  }, [])

  const setWarning = useCallback(warning => {
    dispatch({ type: 'SET_WARNING', warning })
  }, [])

  const setError = useCallback(error => {
    dispatch({ type: 'SET_ERROR', error })
  }, [])

  const setTriggeringRepeats = useCallback(triggering => {
    // Keep isTriggeringRepeats outside of state to avoid recalculating the function it is protecting
    isTriggeringRepeats = triggering
  }, [])

  const setHousekeeping = useCallback(housekeeping => {
    // Keep isHousekeeping outside of state to avoid recalculating the function it is protecting
    isHousekeeping = housekeeping
  }, [])

  const logout = useCallback(async () => {
    await DataStore.stop()
    await DataStore.clear()
    await Auth.signOut()
  }, [])

  const setCallingBackend = useCallback(callingBackend => {
    callingBackend ? callingBackendCounter++ : callingBackendCounter--
    callingBackendCounter = Math.max(0, callingBackendCounter)
    dispatch({
      type: 'SET_IS_CALLING_BACKEND',
      isCallingBackend: callingBackendCounter > 0
    })
  }, [])

  const updateCallingBackend = useCallback(callingBackendCount => {
    callingBackendCounter += callingBackendCount
    callingBackendCounter = Math.max(0, callingBackendCounter)
    dispatch({
      type: 'SET_IS_CALLING_BACKEND',
      isCallingBackend: callingBackendCounter > 0
    })
  }, [])

  // Syncing
  const setSyncing = useCallback(isSyncing => {
    dispatch({ type: 'SET_IS_SYNCING', isSyncing })
  }, [])

  // Open settings drawer and show plans
  const setShowSubscriptionPlans = useCallback(showPlans => {
    dispatch({ type: 'SHOW_SUBSCRIPTION_PLANS', showPlans })
  }, [])

  // Open Area Filter
  const setOpenAreaFilter = useCallback(showAreaFilter => {
    dispatch({ type: 'OPEN_AREA_FILTER', showAreaFilter })
  }, [])

  // Navigation
  const getNavigationItemByPath = useCallback(
    path => navigation.find(element => pathToRegexp(element.path).test(path)),
    []
  )

  const handleSelect = useCallback(
    item => {
      history.push(item.path)
    },
    [history]
  )

  const handleRedirect = useCallback(
    uri => {
      // Create a copy, so we don't change the underlying navigation
      const newNavigationItem = JSON.parse(
        JSON.stringify(getNavigationItemByPath(uri))
      )
      newNavigationItem.path = uri
      handleSelect(newNavigationItem)
    },
    [handleSelect, getNavigationItemByPath]
  )

  // Debounced Updates
  const doObserverUpdate = useCallback(
    (actionType, actionObjectName, actionItems) => {
      LOG.debug('Updating state with observer cache', actionItems)
      dispatch({ type: actionType, [actionObjectName]: actionItems })
      updateCallingBackend(actionItems.length * -1)
      actionItems.length = 0
    },
    [updateCallingBackend]
  )

  const debouncedUpdateAreas = useCallback(
    _.debounce(() => {
      doObserverUpdate('UPDATE_AREAS', 'areas', debounceCache.update.areas)
    }, OBSERVER_DEBOUNCE_INTERVAL),
    []
  )

  const debouncedDeleteAreas = useCallback(
    _.debounce(() => {
      doObserverUpdate('REMOVE_AREAS', 'areas', debounceCache.delete.areas)
    }, OBSERVER_DEBOUNCE_INTERVAL),
    []
  )

  const debouncedUpdateAttachments = useCallback(
    _.debounce(() => {
      doObserverUpdate(
        'UPDATE_ATTACHMENTS',
        'attachments',
        debounceCache.update.attachments
      )
    }, OBSERVER_DEBOUNCE_INTERVAL),
    []
  )

  const debouncedDeleteAttachments = useCallback(
    _.debounce(() => {
      doObserverUpdate(
        'REMOVE_ATTACHMENTS',
        'attachments',
        debounceCache.delete.attachments
      )
    }, OBSERVER_DEBOUNCE_INTERVAL),
    []
  )

  const debouncedUpdateProjects = useCallback(
    _.debounce(() => {
      doObserverUpdate(
        'UPDATE_PROJECTS',
        'projects',
        debounceCache.update.projects
      )
    }, OBSERVER_DEBOUNCE_INTERVAL),
    []
  )

  const debouncedDeleteProjects = useCallback(
    _.debounce(() => {
      doObserverUpdate(
        'REMOVE_PROJECTS',
        'projects',
        debounceCache.delete.projects
      )
    }, OBSERVER_DEBOUNCE_INTERVAL),
    []
  )

  const debouncedUpdateRepeats = useCallback(
    _.debounce(() => {
      doObserverUpdate(
        'UPDATE_REPEATS',
        'repeats',
        debounceCache.update.repeats
      )
    }, OBSERVER_DEBOUNCE_INTERVAL),
    []
  )

  const debouncedDeleteRepeats = useCallback(
    _.debounce(() => {
      doObserverUpdate(
        'REMOVE_REPEATS',
        'repeats',
        debounceCache.delete.repeats
      )
    }, OBSERVER_DEBOUNCE_INTERVAL),
    []
  )

  const debouncedUpdateTags = useCallback(
    _.debounce(() => {
      doObserverUpdate('UPDATE_TAGS', 'tags', debounceCache.update.tags)
    }, OBSERVER_DEBOUNCE_INTERVAL),
    []
  )

  const debouncedDeleteTags = useCallback(
    _.debounce(() => {
      doObserverUpdate('REMOVE_TAGS', 'tags', debounceCache.delete.tags)
    }, OBSERVER_DEBOUNCE_INTERVAL),
    []
  )

  const debouncedUpdateTodos = useCallback(
    _.debounce(() => {
      doObserverUpdate('UPDATE_TODOS', 'todos', debounceCache.update.todos)
    }, OBSERVER_DEBOUNCE_INTERVAL),
    []
  )

  const debouncedDeleteTodos = useCallback(
    _.debounce(() => {
      doObserverUpdate('REMOVE_TODOS', 'todos', debounceCache.delete.todos)
    }, OBSERVER_DEBOUNCE_INTERVAL),
    []
  )
  const asyncUpdateArea = useCallback(
    area => {
      setCallingBackend(true)
      debounceCache.update.areas.push(area)
      debouncedUpdateAreas()
    },
    [debouncedUpdateAreas, setCallingBackend]
  )

  const asyncDeleteArea = useCallback(
    area => {
      setCallingBackend(true)
      debounceCache.delete.areas.push(area)
      debouncedDeleteAreas()
    },
    [debouncedDeleteAreas, setCallingBackend]
  )

  const asyncUpdateAttachment = useCallback(
    attachment => {
      setCallingBackend(true)
      debounceCache.update.attachments.push(attachment)
      debouncedUpdateAttachments()
    },
    [debouncedUpdateAttachments, setCallingBackend]
  )

  const asyncDeleteAttachment = useCallback(
    attachment => {
      setCallingBackend(true)
      debounceCache.delete.attachments.push(attachment)
      debouncedDeleteAttachments()
    },
    [debouncedDeleteAttachments, setCallingBackend]
  )

  const asyncUpdateProject = useCallback(
    project => {
      setCallingBackend(true)
      debounceCache.update.projects.push(project)
      debouncedUpdateProjects()
    },
    [debouncedUpdateProjects, setCallingBackend]
  )

  const asyncDeleteProject = useCallback(
    project => {
      setCallingBackend(true)
      debounceCache.delete.projects.push(project)
      debouncedDeleteProjects()
    },
    [debouncedDeleteProjects, setCallingBackend]
  )

  const asyncUpdateRepeat = useCallback(
    repeat => {
      setCallingBackend(true)
      debounceCache.update.repeats.push(repeat)
      debouncedUpdateRepeats()
    },
    [debouncedUpdateRepeats, setCallingBackend]
  )

  const asyncDeleteRepeat = useCallback(
    repeat => {
      setCallingBackend(true)
      debounceCache.delete.repeats.push(repeat)
      debouncedDeleteRepeats()
    },
    [debouncedDeleteRepeats, setCallingBackend]
  )

  const asyncUpdateTag = useCallback(
    tag => {
      setCallingBackend(true)
      debounceCache.update.tags.push(tag)
      debouncedUpdateTags()
    },
    [debouncedUpdateTags, setCallingBackend]
  )

  const asyncDeleteTag = useCallback(
    tag => {
      setCallingBackend(true)
      debounceCache.delete.tags.push(tag)
      debouncedDeleteTags()
    },
    [debouncedDeleteTags, setCallingBackend]
  )

  const asyncUpdateTodo = useCallback(
    todo => {
      setCallingBackend(true)
      debounceCache.update.todos.push(todo)
      debouncedUpdateTodos()
    },
    [debouncedUpdateTodos, setCallingBackend]
  )

  const asyncUpdateTodos = useCallback(
    todos => {
      setCallingBackend(true)
      debounceCache.update.todos = [...debounceCache.update.todos, ...todos]
      debouncedUpdateTodos()
    },
    [debouncedUpdateTodos, setCallingBackend]
  )

  const asyncDeleteTodo = useCallback(
    todo => {
      setCallingBackend(true)
      debounceCache.delete.todos.push(todo)
      debouncedDeleteTodos()
    },
    [debouncedDeleteTodos, setCallingBackend]
  )

  const getTagByName = useCallback(
    name => {
      LOG.debug('Lookup Tag from state by Name', name)

      const foundTag = state.tags.find(tag => tag.name === name)
      if (foundTag) {
        LOG.debug('Found Tag', foundTag)

        return foundTag
      }
      LOG.warn('No tag found with name ' + name)
      return null
    },
    [state.tags]
  )

  const getUnfilteredTodos = useCallback(
    (list = null) => {
      const doLookup = () => {
        LOG.debug('Get Unfiltered TODOS', 'Start TODO Filter', list)
        const filtered = sortByPositionHint(
          state.todos.filter(todo => {
            if (
              list &&
              list.length &&
              isValidList(todo.list) &&
              todo.list !== list
            ) {
              return false
            }
            return true
          }),
          decrypt
        )

        LOG.debug('Get TODOS', 'Finish TODO Filter')
        return filtered
      }

      return doLookup()
    },
    [state.todos]
  )

  const getTodos = useCallback(
    (
      list = null,
      current = true,
      future = false,
      textFilter = null,
      nameFilter = null,
      descriptionFilter = null,
      tagsFilter = null,
      projectFilter = null,
      sortBy = null
    ) => {
      const today = moment()
      const sortFunction = sortBy ? sortBy : sortByPositionHint

      const checkTextFilter = (text, filter) => {
        if (filter && filter.length) {
          if (!text || !text.toUpperCase().includes(filter.toUpperCase())) {
            return false
          }
        }
        return true
      }

      const doLookup = () => {
        LOG.debug(
          'Get Filtered TODOS',
          list,
          textFilter,
          tagsFilter,
          projectFilter
        )
        LOG.debug('Get TODOS', 'Start TODO Filter')
        const filtered = sortFunction(
          state.todos.filter(todo => {
            const startDate = moment(todo.start)

            if (state.areaFilter && state.areaFilter.length) {
              const validTodoArea =
                todo.area &&
                state.areas.findIndex(area => area.id === todo.area) !== -1

              if (
                !validTodoArea &&
                state.areaFilter.findIndex(area => area.id === NO_AREA.id) ===
                  -1
              ) {
                return false
              }

              if (
                validTodoArea &&
                state.areaFilter.findIndex(area => area.id === todo.area) === -1
              ) {
                return false
              }
            }

            if (!future || !current) {
              if (future) {
                if (!startDate.isValid() || !startDate.isAfter(today)) {
                  LOG.debug('Todo is current', todo, todo.start)
                  return false
                }
              } else if (startDate.isValid() && startDate.isAfter(today)) {
                LOG.debug('Todo is scheduled for the future', todo, todo.start)
                return false
              }
            }

            if (list && list.length) {
              if (isValidList(todo.list)) {
                if (todo.list !== list) {
                  return false
                }
              } else if (list !== FOCUS.INBOX.id) {
                return false
              }
            }

            if (textFilter && textFilter.length) {
              if (
                !checkTextFilter(decrypt(todo.name), textFilter) &&
                !checkTextFilter(decrypt(todo.description), textFilter)
              ) {
                return false
              }
            }

            if (nameFilter && nameFilter.length) {
              if (!checkTextFilter(decrypt(todo.name), nameFilter)) {
                return false
              }
            }

            if (descriptionFilter && descriptionFilter.length) {
              if (
                checkTextFilter(decrypt(todo.description), descriptionFilter)
              ) {
                return false
              }
            }

            if (tagsFilter && tagsFilter.length) {
              if (
                !tagsFilter.every(tagName => {
                  if (todo.tags) {
                    const tag = getTagByName(tagName)
                    if (tag) {
                      return todo.tags.includes(tag.id)
                    }
                  }
                  return false
                })
              ) {
                return false
              }
            }

            if (projectFilter && projectFilter.length) {
              if (!todo.project || todo.project !== projectFilter) {
                return false
              }
            }

            return true
          }),
          decrypt
        )
        LOG.debug('Get TODOS', 'Finish TODO Filter')
        return filtered
      }

      // Start by not caching everything, just the main lists
      if (
        list &&
        !textFilter &&
        !nameFilter &&
        !descriptionFilter &&
        (!tagsFilter || !tagsFilter.length)
      ) {
        const subKey = `${textFilter}:${nameFilter}:${descriptionFilter}:${tagsFilter}:${projectFilter}:${state.areaFilter.map(
          area => area.id
        )}:${today.format('YYYY-MM-DD')}:${current}:${future}`
        LOG.debug('Get TODOs from Cache', list, subKey)
        return todoCache.lookupSubKey(list, subKey, doLookup)
      } else {
        LOG.debug('Calculate which TODOs to return')
        return doLookup()
      }
    },
    [state.areaFilter, state.areas, state.todos, getTagByName]
  )

  const getNewTodoTopPosition = useCallback(
    list => {
      const todosForList = getTodos(list)
      const position =
        todosForList && todosForList.length ? todosForList[0].position - 1 : 0
      LOG.debug('Top Position', position)
      return position
    },
    [getTodos]
  )

  const getTodoById = useCallback(
    id => {
      LOG.debug('Lookup TODO from state by ID', id)

      const foundTodo = state.todos.find(todo => todo.id === id)
      if (foundTodo) {
        LOG.debug('Found TODO', foundTodo)

        return foundTodo
      }
      LOG.warn('No todo found with id ' + id)
      return null
    },
    [state.todos]
  )

  const saveTodos = useCallback(
    async todosToSave => {
      LOG.debug('Saving these TODOs', todosToSave)

      let showError = true

      todosToSave.forEach(todo => {
        setCallingBackend(true)
        LOG.debug('Save Todo', decrypt(todo.name) + ':' + todo.position)

        const saveTodo = Todo.copyOf(getTodoById(todo.id) || todo, updated => {
          // This is a workaround as we can only do copyOf in one go as
          // it indicates what fields are being mutated
          Object.assign(updated, todo)
        })
        DataStore.save(saveTodo)
          .then(savedTodo => {
            LOG.debug(
              'Saved Todo',
              decrypt(savedTodo.name) + ':' + savedTodo.position
            )
          })
          .catch(saveError => {
            LOG.error(
              'Save Todo Failed',
              decrypt(todo.name) + ':' + todo.position
            )
            // Restore todo, so state matches the data
            dispatch({
              type: 'UPDATE_TODO',
              todo: getTodoById(todo.id)
            })

            if (showError) {
              showError = false
              setError('Failed to save todo: ' + saveError)
            }
          })
          .finally(() => setCallingBackend(false))
      })
    },
    [setCallingBackend, getTodoById, setError]
  )

  const updateTodoPositionsReturnChanged = useCallback(todos => {
    return TodoPositions.updateTodoPositionsReturnChanged(CLIENT_ID, todos)
  }, [])

  const getTagById = useCallback(
    id => {
      LOG.debug('Lookup Tag from state by ID', id)

      const foundTag = state.tags.find(tag => tag.id === id)
      if (foundTag) {
        LOG.debug('Found Tag', foundTag)

        return foundTag
      }
      LOG.warn('No tag found with id ' + id)
      return null
    },
    [state.tags]
  )

  const getTags = useCallback(() => {
    return state.tags
  }, [state.tags])

  const getAvailableTags = useCallback(
    (list = null) => {
      LOG.debug('Get Available Tags', list)

      const tags = [
        ...new Set(
          [].concat(
            ...new Set(
              getTodos(list, true, true).map(todo => {
                return todo.tags ? todo.tags.split(',') : []
              })
            )
          )
        )
      ]
        .map(tagId => {
          const tag = getTagById(tagId)
          if (tag) {
            return tag.name
          }
          return null
        })
        .filter(tag => tag !== null)

      LOG.debug(tags)
      return sortByName(tags)
    },
    [getTodos, getTagById]
  )

  const createTag = useCallback(
    async (name, performDispatch = true) => {
      LOG.debug('Create Tag', name)

      if (!name) {
        LOG.warn('Name is mandatory')
        return
      }

      setCallingBackend(true)
      const tag = await DataStore.save(
        new Tag({
          owner: state.owner,
          clientId: CLIENT_ID,
          name
        })
      )
        .catch(err => {
          setError('Failed to create tag: ' + err)
          throw err
        })
        .finally(() => setCallingBackend(false))

      if (performDispatch) {
        dispatch({ type: 'ADD_TAG', tag })
      } else {
        asyncUpdateTag(tag)
      }
      return tag
    },
    [state.owner, setCallingBackend, setError, asyncUpdateTag]
  )

  const deleteTag = useCallback(
    async tag => {
      LOG.debug('Deleting tag: ' + tag)

      setCallingBackend(true)
      DataStore.delete(tag)
        .catch(err => {
          setError('Failed to delete tag: ' + err)
          throw err
        })
        .finally(() => setCallingBackend(false))

      dispatch({ type: 'REMOVE_TAG', tag })
    },
    [setCallingBackend, setError]
  )

  const getTagsFromTodo = useCallback(
    todo => {
      if (todo.tags) {
        return todo.tags
          .split(',')
          .map(tagId => {
            const tag = getTagById(tagId)
            if (tag) {
              return tag.name
            }
            return null
          })
          .filter(tagName => tagName !== null)
          .sort()
      } else {
        return []
      }
    },
    [getTagById]
  )

  const createFullTodo = useCallback(
    async (
      name,
      description,
      status,
      tags,
      area,
      project,
      list,
      start,
      repeat,
      top = true,
      performDispatch = true
    ) => {
      LOG.debug(
        'Create Full TODO',
        name,
        description,
        status,
        tags,
        area,
        project,
        list,
        start,
        top
      )

      if (!name) {
        LOG.warn('Name is mandatory')
        return
      }

      const newTodo = new Todo({
        owner: state.owner,
        clientId: CLIENT_ID,
        name: encrypt(name),
        description: description ? encrypt(description) : null,
        status,
        tags,
        area: area ? area : null,
        project: project ? project : null,
        list,
        start,
        repeat,
        position: top ? getNewTodoTopPosition(list) : null
      })

      LOG.debug('Add new TODO to list array', newTodo)
      const updatedTodos = updateTodoPositionsReturnChanged(
        top ? [newTodo, ...getTodos(list)] : [...getTodos(list), newTodo]
      )
      if (updatedTodos.findIndex(todo => todo.id === newTodo.id) < 0) {
        updatedTodos.push(newTodo)
      }

      saveTodos(updatedTodos)
      if (performDispatch) {
        dispatch({ type: 'UPDATE_TODOS', todos: updatedTodos })
      } else {
        asyncUpdateTodos(updatedTodos)
      }

      return newTodo
    },
    [
      getNewTodoTopPosition,
      state.owner,
      saveTodos,
      getTodos,
      updateTodoPositionsReturnChanged,
      asyncUpdateTodos
    ]
  )

  const getRepeats = useCallback(() => {
    return state.repeats
  }, [state.repeats])

  const getRepeatById = useCallback(
    id => {
      LOG.debug('Lookup Repeat from state by ID', id)

      const foundRepeat = state.repeats.find(repeat => repeat.id === id)
      if (foundRepeat) {
        LOG.debug('Found Repeat', foundRepeat)

        return foundRepeat
      }
      LOG.warn('No repeat found with id ' + id)
      return null
    },
    [state.repeats]
  )

  const createRepeat = useCallback(
    async (pattern, todo, startDate = moment().format('YYYY-MM-DD')) => {
      LOG.debug('Create Repeat', pattern, todo)

      if (!pattern) {
        LOG.warn('Pattern is mandatory')
        return
      }

      if (!todo) {
        LOG.warn('TODO is mandatory')
        return
      }

      setCallingBackend(true)
      const repeat = await DataStore.save(
        new Repeat({
          owner: state.owner,
          clientId: CLIENT_ID,
          pattern,
          startDate,
          lastUpdate: null
        })
      )
        .catch(err => {
          setError('Failed to create repeat: ' + err)
          throw err
        })
        .finally(() => setCallingBackend(false))

      LOG.debug('Created Repeat', repeat)
      dispatch({ type: 'ADD_REPEAT', repeat })

      const nextDate = new RepeatPattern(pattern).nextDate(
        moment(startDate).toDate(),
        false
      )

      createFullTodo(
        decrypt(todo.name),
        decrypt(todo.description),
        null,
        todo.tags,
        todo.area,
        todo.project,
        FOCUS.REPEATING.id,
        nextDate ? moment(nextDate).format('YYYY-MM-DD') : null,
        repeat.id,
        false,
        true
      )
      return repeat
    },
    [createFullTodo, state.owner, setCallingBackend, setError]
  )

  const getRepeatPattern = useCallback(
    todo => {
      LOG.debug('Get Repeat Pattern', todo)

      if (todo && todo.repeat) {
        const repeat = getRepeatById(todo.repeat)
        if (repeat) {
          return repeat.pattern
        }
      }
      return null
    },
    [getRepeatById]
  )

  const deleteRepeat = useCallback(
    async id => {
      if (id) {
        LOG.debug('Deleting repeat: ' + id)

        const repeat = getRepeatById(id)

        if (repeat) {
          setCallingBackend(true)
          DataStore.delete(repeat)
            .catch(err => {
              setError('Failed to delete repeat: ' + err)
              throw err
            })
            .finally(() => setCallingBackend(false))

          dispatch({ type: 'REMOVE_REPEAT', repeat })
        }
      } else {
        LOG.warn('Cannot delete unspecified repeat')
      }
    },
    [getRepeatById, setCallingBackend, setError]
  )

  const editRepeat = useCallback(
    (repeat, todo) => {
      LOG.debug('Edit Repeat', repeat.id)

      setCallingBackend(true)
      return DataStore.save(repeat)
        .then(updatedRepeat =>
          dispatch({ type: 'UPDATE_REPEAT', repeat: updatedRepeat })
        )
        .catch(err => setError('Failed to edit repeat: ' + err))
        .finally(() => setCallingBackend(false))
    },
    [setCallingBackend, setError]
  )

  const editRepeatPattern = useCallback(
    (id, pattern) => {
      LOG.debug('Edit Repeat', id, pattern)

      return editRepeat(
        Repeat.copyOf(getRepeatById(id), updated => {
          updated.clientId = CLIENT_ID
          updated.pattern = pattern
          updated.startDate = moment().format('YYYY-MM-DD')
        })
      )
    },
    [getRepeatById, editRepeat]
  )

  const getAttachmentById = useCallback(
    id => {
      LOG.debug('Lookup Attachment from state by ID', id)

      const foundAttachment = state.attachments.find(
        attachment => attachment.id === id
      )
      if (foundAttachment) {
        LOG.debug('Found Attachment', foundAttachment)

        return foundAttachment
      }
      LOG.warn('No attachment found with id ' + id)
      return null
    },
    [state.attachments]
  )

  const getUnfilteredAttachments = useCallback(() => {
    return state.attachments
  }, [state.attachments])

  const getTodoAttachments = useCallback(
    todo => {
      const attachments = todo.attachments ? todo.attachments.split(',') : []
      return attachments
        .map(attachmentId => getAttachmentById(attachmentId))
        .filter(attachment => attachment !== null)
    },
    [getAttachmentById]
  )

  const createAttachment = useCallback(
    (
      todo,
      file,
      progressCallback = () => {},
      completeCallback = () => {},
      performDispatch = true
    ) => {
      LOG.debug('Create Attachment', todo, file)

      if (!file) {
        LOG.warn('File is mandatory')
        completeCallback()
        return
      }

      if (file.size > MAX_ATTACHMENT_BYTES) {
        setError('Attachment exceeds maximum permitted size')
        completeCallback()
        return
      }

      if (file.size + state.attachmentsSize > MAX_STORAGE_BYTES) {
        setError(
          'You do not have enough storage available to add any further attachments'
        )
        completeCallback()
        return
      }

      const attachment = new Attachment({
        owner: state.owner,
        clientId: CLIENT_ID,
        key: uuidv4(), // Use an explicit key, rather than id as the key, to help export/import
        filename: file.name,
        name: file.name,
        type: file.type,
        size: file.size
      })

      LOG.info('Attachment', attachment)

      setCallingBackend(true)
      Storage.put(attachment.key, file, {
        contentType: file.type,
        progressCallback(progress) {
          progressCallback((100 * progress.loaded) / progress.total)
        }
      })
        .then(() => {
          LOG.debug('Attachment added to storage')

          DataStore.save(attachment)
            .then(() => {
              LOG.debug('Attachment saved')

              if (performDispatch) {
                dispatch({ type: 'ADD_ATTACHMENT', attachment })
              } else {
                asyncUpdateAttachment(attachment)
              }
              DataStore.save(
                Todo.copyOf(todo, updated => {
                  updated.clientId = CLIENT_ID
                  updated.attachments =
                    (updated.attachments ? updated.attachments + ',' : '') +
                    attachment.id
                })
              )
                .then(updatedTodo => {
                  LOG.debug('Attachment added to todo')

                  if (performDispatch) {
                    dispatch({ type: 'UPDATE_TODO', todo: updatedTodo })
                  } else {
                    asyncUpdateTodo(updatedTodo)
                  }
                })
                .catch(err => {
                  setError('Failed to add attachment to TODO: ' + err)
                })
            })
            .catch(err => {
              setError('Failed to create attachment: ' + err)
            })
        })
        .catch(err => {
          setError('Failed to save attachment: ' + err)
        })
        .finally(() => {
          completeCallback()
          setCallingBackend(false)
        })
    },
    [
      asyncUpdateAttachment,
      asyncUpdateTodo,
      setCallingBackend,
      setError,
      state.attachmentsSize,
      state.owner
    ]
  )

  const deleteAttachment = useCallback(
    (attachment, todo, performDispatch = true) => {
      LOG.info('Delete Attachment', attachment.name)

      const doDelete = () => {
        DataStore.delete(attachment)
          .then(() => {
            if (performDispatch) {
              dispatch({ type: 'REMOVE_ATTACHMENT', attachment })
            } else {
              asyncDeleteAttachment(attachment)
            }

            Storage.remove(attachment.key)
              .then(() => {
                LOG.debug('Attachment removed from storage')
              })
              .catch(err => {
                setError('Failed to remove attachment from storage: ' + err)
              })
          })
          .catch(err => {
            setError('Failed to remove attachment: ' + err)
          })
          .finally(() => setCallingBackend(false))
      }

      setCallingBackend(true)

      if (todo) {
        const remainingAttachments = todo.attachments
          .split(',')
          .filter(attachmentId => attachmentId !== attachment.id)

        DataStore.save(
          Todo.copyOf(todo, updated => {
            updated.clientId = CLIENT_ID
            updated.attachments = remainingAttachments.length
              ? remainingAttachments.toString()
              : null
          })
        )
          .then(updatedTodo => {
            LOG.debug('Attachment removed from todo')

            if (performDispatch) {
              dispatch({ type: 'UPDATE_TODO', todo: updatedTodo })
            } else {
              asyncUpdateTodo(updatedTodo)
            }

            doDelete()
          })
          .catch(err => {
            setError('Failed to remove attachment from TODO: ' + err)
          })
      } else {
        doDelete()
      }
    },
    [asyncDeleteAttachment, asyncUpdateTodo, setCallingBackend, setError]
  )

  const downloadAttachment = useCallback((attachment, viewInTab = false) => {
    // https://docs.amplify.aws/lib/storage/download/q/platform/js#file-download-option
    const downloadBlob = (blob, filename) => {
      if (viewInTab) {
        const url = URL.createObjectURL(blob)
        window.open(url, '_blank')
      } else {
        // Use FileSaver for download as it'll present an option on iOS
        // iOS Safari doesn't support <a download>
        FileSaver.saveAs(blob, filename)
      }
    }

    LOG.info('Download', attachment.filename)

    Storage.get(attachment.key, { download: true }).then(res =>
      downloadBlob(res.Body, attachment.filename)
    )
  }, [])

  const editAttachment = useCallback(
    (attachment, name) => {
      DataStore.save(
        Attachment.copyOf(attachment, updated => {
          updated.clientId = CLIENT_ID
          updated.name = name
        })
      )
        .then(updatedAttachment => {
          LOG.debug('Attachment removed from todo')

          dispatch({ type: 'UPDATE_ATTACHMENT', attachment: updatedAttachment })
        })
        .catch(err => {
          setError('Failed to edit attachment name: ' + err)
        })
    },
    [setError]
  )

  const deleteTodo = useCallback(
    (todo, performDispatch = true) => {
      if (todo) {
        LOG.debug('Deleting TODO', todo.id)
        if (todo.list === FOCUS.REPEATING.id) {
          deleteRepeat(todo.repeat)
        }

        if (todo.attachments) {
          todo.attachments.split(',').forEach(attachmentId => {
            const attachment = getAttachmentById(attachmentId)
            if (attachment) {
              // Don't provide the todo, as we don't want it updated
              deleteAttachment(attachment, null, performDispatch)
            }
          })
        }

        setCallingBackend(true)
        DataStore.delete(todo)
          .catch(err => setError('Failed to delete todo: ' + err))
          .finally(() => setCallingBackend(false))

        if (performDispatch) {
          dispatch({ type: 'REMOVE_TODO', todo })
        } else {
          asyncDeleteTodo(todo)
        }
      } else {
        LOG.warn('Cannot delete unspecified TODO', todo)
      }
    },
    [
      deleteAttachment,
      deleteRepeat,
      getAttachmentById,
      setCallingBackend,
      setError,
      asyncDeleteTodo
    ]
  )

  const updateRepeatFromTodo = useCallback(
    async (pattern, todo) => {
      LOG.debug('Update Repeat from TODO', pattern, todo)

      let repeat = todo.repeat

      if (pattern && pattern.length > 0) {
        if (!getRepeatById(todo.repeat)) {
          LOG.debug('No repeat assigned to TODO, create a new one')
          const newRepeat = await createRepeat(pattern, todo)
          LOG.debug('Repeat is', newRepeat)
          repeat = newRepeat.id
        } else {
          LOG.debug('Edit existing repeat')
          editRepeatPattern(todo.repeat, pattern)
        }
      } else if (repeat) {
        deleteRepeat(todo.repeat)
        getTodos(FOCUS.REPEATING.id)
          .filter(repeatingTodo => repeatingTodo.repeat === repeat.id)
          .forEach(repeatingTodo => deleteTodo(repeatingTodo))
        repeat = null
      }

      LOG.debug('Updated Repeat', repeat)
      return repeat
    },
    [
      getTodos,
      deleteTodo,
      createRepeat,
      deleteRepeat,
      editRepeatPattern,
      getRepeatById
    ]
  )

  const getOutstandingTodoListCount = useCallback(
    (list, current = true, future = false) => {
      return countCache.lookupSubKey(
        list,
        state.areaFilter.map(area => area.id) + ':' + future,
        () => {
          LOG.debug('Get count of outstanding TODOs', list)
          let count = 0
          getTodos(list, current, future).forEach(todo => {
            if (todo && todo.status !== 'done') {
              count++
            }
          })
          return count
        }
      )
    },
    [state.areaFilter, getTodos]
  )

  const getProjectById = useCallback(
    id => {
      LOG.debug('Lookup Project from state by ID', id)

      const foundProject = state.projects.find(project => project.id === id)
      if (foundProject) {
        LOG.debug('Found Project', foundProject)

        return foundProject
      }
      LOG.debug('No project found with id ' + id)
      return null
    },
    [state.projects]
  )

  const createTodo = useCallback(
    async (name, list, projectId, top = true) => {
      LOG.info('Create TODO', name, list, projectId, top)

      console.log(decrypt(encrypt('£££ abc £££')))
      let area =
        state.areaFilter && state.areaFilter.length === 1
          ? state.areaFilter[0].id
          : null

      if (projectId) {
        const project = getProjectById(projectId)
        if (project) {
          area = project.area
        }
      }

      return createFullTodo(
        name,
        null,
        null,
        null,
        area,
        projectId,
        list,
        null,
        null,
        top
      )
    },
    [state.areaFilter, createFullTodo, getProjectById]
  )

  const moveTodoToList = useCallback(
    (todo, list, position) => {
      LOG.debug('Move TODO to List', todo, list, position)

      setCallingBackend(true)
      DataStore.save(
        Todo.copyOf(todo, updated => {
          updated.clientId = CLIENT_ID
          updated.list = list
          updated.position = position
        })
      )
        .then(updatedTodo => {
          dispatch({ type: 'UPDATE_TODO', todo: updatedTodo })
        })
        .catch(err => setError(`Failed to move todo to list ${list}:` + err))
        .finally(() => setCallingBackend(false))
    },
    [setCallingBackend, setError]
  )

  const confirmPromoteTodo = useCallback((todo, position) => {
    LOG.debug('Confirm Promote TODO', todo, position)

    dispatch({ type: 'CONFIRM_PROMOTE_TODO', todo, position })
  }, [])

  const confirmDeleteProject = useCallback(project => {
    LOG.debug('Confirm Delete Project', project)

    dispatch({ type: 'CONFIRM_DELETE_PROJECT', project })
  }, [])

  const autoPromoteProject = useCallback(
    todo => {
      if (todo.project) {
        LOG.info('Check if todos need promoting or project deleting')
        const project = getProjectById(todo.project)
        if (project) {
          if (project.autoPromote || project.autoDelete) {
            const currentTodos = getTodos(
              null,
              true,
              false,
              null,
              null,
              null,
              null,
              project.id
            )

            const promoteTodos = currentTodos.filter(
              projectTodo =>
                projectTodo.list === FOCUS.NEXT.id &&
                projectTodo.status !== 'done'
            )
            if (promoteTodos.length) {
              LOG.info('There are todos that could be promoted')
              if (project.autoPromote) {
                LOG.info('Project is set to auto promote')
                const nowTodos = currentTodos.filter(
                  projectTodo =>
                    projectTodo.list === FOCUS.TODAY.id &&
                    projectTodo.status !== 'done'
                )
                if (
                  nowTodos.length === 0 ||
                  (todo.status === 'done' &&
                    nowTodos.length === 1 &&
                    nowTodos[0].id === todo.id) // Todo has just been updated
                ) {
                  LOG.info('There are no other todos for today')
                  confirmPromoteTodo(promoteTodos[0], todo.position)
                }
              }
            } else if (project.autoDelete) {
              const allTodos = getTodos(
                null,
                true,
                true,
                null,
                null,
                null,
                null,
                project.id
              ).filter(
                projectTodo =>
                  projectTodo.list !== FOCUS.TRASH.id &&
                  projectTodo.status !== 'done'
              )
              if (
                allTodos.length === 0 ||
                (todo.status === 'done' &&
                  allTodos.length === 1 &&
                  allTodos[0].id === todo.id) // Todo has just been updated
              ) {
                confirmDeleteProject(project)
              }
            }
          }
        }
      }
    },
    [getProjectById, getTodos, confirmDeleteProject, confirmPromoteTodo]
  )

  const editTodo = useCallback(
    async (
      id,
      name,
      description,
      status,
      tagNames,
      area,
      projectId,
      list,
      start, // YYYY-MM-DD
      repeatPattern
    ) => {
      LOG.debug(
        'Edit TODO',
        id,
        name,
        description,
        status,
        tagNames,
        area,
        projectId,
        list,
        start,
        repeatPattern
      )

      const originalTodo = getTodoById(id)

      const repeat = await updateRepeatFromTodo(repeatPattern, originalTodo)

      const doEdit = tagIds => {
        setCallingBackend(true)
        DataStore.save(
          Todo.copyOf(originalTodo, updated => {
            updated.clientId = CLIENT_ID
            updated.name = encrypt(name)
            updated.description =
              description && description.length ? encrypt(description) : null // Can't supply ""
            updated.status = status ? status : null
            updated.tags = tagIds && tagIds.length ? tagIds.toString() : null // Can't supply ""
            updated.list = list
            updated.area = area && area.length ? area : null
            updated.project = projectId && projectId.length ? projectId : null
            updated.start = start ? start : null
            updated.repeat = repeat ? repeat : null
            updated.completed =
              updated.completed !== 'done' && status === 'done'
                ? moment().format('YYYY-MM-DD')
                : null
          })
        )
          .then(updatedTodo => {
            dispatch({ type: 'UPDATE_TODO', todo: updatedTodo })

            if (status === 'done' && originalTodo.status !== 'done') {
              autoPromoteProject(updatedTodo)
            }
          })
          .catch(err => setError('Failed to edit todo: ' + err))
          .finally(() => setCallingBackend(false))
      }

      if (tagNames && tagNames.length) {
        // Make sure the tags are saved, before we update the Todo
        const tags = []
        processLargeArrayAsync(
          'Save Tags',
          tagNames,
          async tagName => {
            const tag = getTagByName(tagName)
            if (tag) {
              tags.push(tag.id)
            } else {
              setCallingBackend(true)
              await DataStore.save(
                new Tag({
                  owner: state.owner,
                  clientId: CLIENT_ID,
                  name: tagName
                })
              )
                .then(newTag => {
                  dispatch({ type: 'ADD_TAG', tag: newTag })
                  tags.push(newTag.id)
                })
                .catch(err => {
                  setError('Failed to create tag: ' + err)
                  throw err
                })
                .finally(() => setCallingBackend(false))
            }
          },
          () => {
            LOG.debug('TODO has tags: ', tags)
            doEdit(tags)
          },
          true
        )
      } else {
        doEdit(null)
      }
    },
    [
      autoPromoteProject,
      getTagByName,
      getTodoById,
      setCallingBackend,
      setError,
      updateRepeatFromTodo,
      state.owner
    ]
  )

  const editTodoStatus = useCallback(
    async (id, status) => {
      LOG.debug('Edit TODO status', id, status)

      const originalTodo = getTodoById(id)

      setCallingBackend(true)
      DataStore.save(
        Todo.copyOf(originalTodo, updated => {
          updated.clientId = CLIENT_ID
          updated.status = status
          updated.completed =
            updated.completed !== 'done' && status === 'done'
              ? moment().format('YYYY-MM-DD')
              : null
        })
      )
        .then(todo => {
          dispatch({ type: 'UPDATE_TODO', todo })

          if (status === 'done' && originalTodo.status !== 'done') {
            autoPromoteProject(todo)
          }
        })
        .catch(err => setError('Failed to edit todo status: ' + err))
        .finally(() => setCallingBackend(false))
    },
    [autoPromoteProject, getTodoById, setCallingBackend, setError]
  )

  const editTodoProject = useCallback(
    async (todoId, projectId) => {
      LOG.debug('Add TODO to Project', todoId, projectId)

      const project = getProjectById(projectId)
      if (project) {
        setCallingBackend(true)
        DataStore.save(
          Todo.copyOf(getTodoById(todoId), updated => {
            updated.clientId = CLIENT_ID
            updated.area = project.area ? project.area : null // explicit null
            updated.project = project.id
          })
        )
          .then(todo => {
            dispatch({ type: 'UPDATE_TODO', todo })
          })
          .catch(err => setError('Failed to edit todo project: ' + err))
          .finally(() => setCallingBackend(false))
      } else {
        setError('Project not found, has it been deleted?')
      }
    },
    [getProjectById, getTodoById, setCallingBackend, setError]
  )

  const editTodoStart = useCallback(
    async (todoId, startDate) => {
      LOG.debug('edit TODO start date', todoId, startDate)

      setCallingBackend(true)
      return DataStore.save(
        Todo.copyOf(getTodoById(todoId), updated => {
          updated.clientId = CLIENT_ID
          updated.start = moment(startDate).isValid()
            ? moment(startDate).format('YYYY-MM-DD')
            : null
        })
      )
        .then(todo => {
          dispatch({ type: 'UPDATE_TODO', todo })
        })
        .catch(err => setError('Failed to edit todo start date: ' + err))
        .finally(() => setCallingBackend(false))
    },
    [getTodoById, setCallingBackend, setError]
  )

  const moveTodoToTrash = useCallback(
    (todo, performDispatch = true) => {
      // Added this specific function as we don't need to reorder anything for trash
      LOG.debug('Move TODO to trash', todo)

      setCallingBackend(true)
      DataStore.save(
        Todo.copyOf(todo, updated => {
          updated.clientId = CLIENT_ID
          updated.list = FOCUS.TRASH.id
          updated.deleted = moment().format('YYYY-MM-DD')
        })
      )
        .then(updatedTodo => {
          if (performDispatch) {
            dispatch({ type: 'UPDATE_TODO', todo: updatedTodo })
          } else {
            asyncUpdateTodo(updatedTodo)
          }
        })
        .catch(err => setError('Failed to move todo to trash: ' + err))
        .finally(() => setCallingBackend(false))
    },
    [setCallingBackend, setError, asyncUpdateTodo]
  )

  const cacheDragList = useCallback((list, todos) => {
    dispatch({ type: 'CACHE_DRAG_LIST', list, todos })
  }, [])

  const handleTodoDragEnd = useCallback(
    (id, list, sequenceInListArray, changeStatus = false) => {
      LOG.debug(
        'Handling TODO List Drag',
        id,
        list,
        sequenceInListArray,
        changeStatus
      )

      const originalTodo = getTodoById(id)
      let movingTodo = originalTodo
      if (changeStatus !== false) {
        movingTodo = Todo.copyOf(movingTodo, updated => {
          updated.clientId = CLIENT_ID
          updated.status = changeStatus
          updated.completed =
            updated.completed !== 'done' && changeStatus === 'done'
              ? moment().format('YYYY-MM-DD')
              : null
        })
      }

      if (movingTodo.list === list) {
        LOG.debug('Moving within the same list')

        // We want to update Datastore in then background, then update state in one hit
        const todosCopy = [...state.cacheDragList[list]]
        todosCopy.splice(
          todosCopy.findIndex(todo => todo.id === id),
          1
        )
        todosCopy.splice(sequenceInListArray, 0, movingTodo)

        const updatedTodos = updateTodoPositionsReturnChanged(todosCopy)
        if (updatedTodos.findIndex(todo => todo.id === movingTodo.id) < 0) {
          updatedTodos.push(movingTodo)
        }

        saveTodos(updatedTodos)
        dispatch({ type: 'UPDATE_TODOS', todos: updatedTodos })

        if (changeStatus === 'done' && originalTodo.status !== 'done') {
          autoPromoteProject(movingTodo)
        }
      } else {
        LOG.debug('Moving from list', movingTodo.list)

        const sourceTodos = [...state.cacheDragList[movingTodo.list]]

        // We can do this all at once, but do an explicit save on the list change
        // asap, so that it's honoured on refresh
        const movedTodo = Todo.copyOf(movingTodo, updated => {
          updated.clientId = CLIENT_ID
          updated.list = list
        })

        setCallingBackend(true)
        DataStore.save(movedTodo)
          .catch(err => setError(`Failed to move todo to list ${list}:` + err))
          .finally(() => setCallingBackend(false))

        const sourceIndex = sourceTodos.findIndex(todo => todo.id === id)
        if (sourceIndex >= 0) {
          sourceTodos.splice(sourceIndex, 1)
        }
        const destinationTodos = [...(state.cacheDragList[list] || [])]
        destinationTodos.splice(sequenceInListArray, 0, movedTodo)

        const updatedSourceTodos = updateTodoPositionsReturnChanged(sourceTodos)
        const updatedDestTodos =
          updateTodoPositionsReturnChanged(destinationTodos)
        // The position of the moved todo may not have changed,
        // so explicitly add it if necessary
        if (updatedDestTodos.findIndex(todo => todo.id === movedTodo.id) < 0) {
          updatedDestTodos.push(movedTodo)
        }

        saveTodos([...updatedSourceTodos, ...updatedDestTodos])

        dispatch({
          type: 'UPDATE_TODOS',
          todos: [...updatedSourceTodos, ...updatedDestTodos]
        })
      }
    },
    [
      autoPromoteProject,
      getTodoById,
      saveTodos,
      updateTodoPositionsReturnChanged,
      setCallingBackend,
      state.cacheDragList,
      setError
    ]
  )

  const getProjectNameForTodo = useCallback(
    todo => {
      if (todo.project) {
        const project = getProjectById(todo.project)
        if (project) {
          return decrypt(project.name)
        }
      }
      return null
    },
    [getProjectById]
  )

  const getUnfilteredProjects = useCallback(() => {
    return state.projects
  }, [state.projects])

  const getProjects = useCallback(() => {
    return projectCache.lookupKey(
      state.areaFilter.map(area => area.id),
      () =>
        sortByName(
          state.projects.filter(project => {
            if (state.areaFilter && state.areaFilter.length) {
              if (
                state.areaFilter.findIndex(
                  areaFilterItem =>
                    areaFilterItem.id === project.area ||
                    (areaFilterItem.id === NO_AREA.id && !project.area)
                ) === -1
              ) {
                return false
              }
            }
            return true
          })
        )
    )
  }, [state.areaFilter, state.projects])

  const getProjectsForAreas = useCallback(
    areasInScope => {
      LOG.debug('Get Projects for Areas', areasInScope)
      return sortByName(
        state.projects.filter(project => {
          if (areasInScope && areasInScope.length) {
            if (
              !areasInScope.includes('') &&
              areasInScope.findIndex(area => project.area === area) === -1
            ) {
              return false
            }
          }
          return true
        })
      )
    },
    [state.projects]
  )

  const createProject = useCallback(
    async (
      name,
      description = null,
      area = null,
      autoPromote = false,
      autoDelete = false,
      performDispatch = true
    ) => {
      LOG.debug('Create Project', name, description)

      if (!name) {
        LOG.warn('Name is mandatory')
        return
      }

      setCallingBackend(true)
      const project = await DataStore.save(
        new Project({
          owner: state.owner,
          clientId: CLIENT_ID,
          name: encrypt(name),
          description:
            description && description.length ? encrypt(description) : null,
          area: area
            ? area
            : state.areaFilter && state.areaFilter.length === 1
            ? state.areaFilter[0].id
            : null,
          autoPromote,
          autoDelete
        })
      )
        .catch(err => {
          setError('Failed to create project: ' + err)
          throw err
        })
        .finally(() => setCallingBackend(false))

      if (performDispatch) {
        dispatch({ type: 'ADD_PROJECT', project })
      } else {
        asyncUpdateProject(project)
      }
      return project
    },
    [
      state.areaFilter,
      state.owner,
      setCallingBackend,
      setError,
      asyncUpdateProject
    ]
  )

  const deleteProject = useCallback(
    async project => {
      if (project) {
        LOG.debug('Deleting project: ' + project.id)

        setCallingBackend(true)
        await DataStore.delete(project)
          .catch(err => {
            setError('Failed to delete project: ' + err)
            throw err
          })
          .finally(() => setCallingBackend(false))

        dispatch({ type: 'REMOVE_PROJECT', project })
        if (project.id === state.navigationSelectedItem.id) {
          LOG.debug('Redirecting to /')
          handleRedirect('/')
        }
      } else {
        LOG.warn('Cannot delete unspecified Project')
      }
    },
    [
      handleRedirect,
      state.navigationSelectedItem.id,
      setCallingBackend,
      setError
    ]
  )

  const editProject = useCallback(
    async (id, name, description, area, autoPromote, autoDelete) => {
      LOG.debug(
        'Edit Project',
        id,
        name,
        description,
        area,
        autoPromote,
        autoDelete
      )

      const project = getProjectById(id)

      setCallingBackend(true)
      DataStore.save(
        Project.copyOf(project, updated => {
          updated.clientId = CLIENT_ID
          updated.name = encrypt(name)
          updated.description =
            description && description.length ? encrypt(description) : null // Can't supply ""
          updated.area = area
          updated.autoPromote = autoPromote
          updated.autoDelete = autoDelete
        })
      )
        .then(updatedProject => {
          dispatch({ type: 'UPDATE_PROJECT', project: updatedProject })
          if (project.area !== area) {
            const updatedTodos = getTodos(
              null,
              true,
              true,
              null,
              null,
              null,
              null,
              id
            ).map(todo =>
              Todo.copyOf(todo, updated => {
                updated.clientId = CLIENT_ID
                updated.area = area
              })
            )
            saveTodos(updatedTodos)
            dispatch({ type: 'UPDATE_TODOS', todos: updatedTodos })
          }
        })
        .catch(err => setError('Failed to edit project: ' + err))
        .finally(() => setCallingBackend(false))
    },
    [getProjectById, getTodos, saveTodos, setCallingBackend, setError]
  )

  const getAreaById = useCallback(
    id => {
      LOG.debug('Lookup Area from state by ID', id)

      const foundArea = state.areas.find(area => area.id === id)
      if (foundArea) {
        LOG.debug('Found Area', foundArea)

        return foundArea
      }
      LOG.warn('No area found with id ' + id)
      return null
    },
    [state.areas]
  )

  const getAreas = useCallback(
    (includeNoArea = true) => {
      return areaCache.lookupKey(includeNoArea, () => {
        return includeNoArea
          ? [NO_AREA, ...sortByName(state.areas)]
          : sortByName(state.areas)
      })
    },
    [state.areas]
  )

  const createArea = useCallback(
    async (name, colour, performDispatch) => {
      LOG.debug('Create Area', name)

      if (!name) {
        LOG.warn('Name is mandatory')
        return
      }

      const encryptedName = encrypt(name)

      setCallingBackend(true)
      const area = await DataStore.save(
        new Area({
          owner: state.owner,
          clientId: CLIENT_ID,
          name: encryptedName,
          colour: colour ? colour : null
        })
      )
        .catch(err => {
          setError('Failed to create area: ' + err)
          throw err
        })
        .finally(() => setCallingBackend(false))

      if (performDispatch) {
        dispatch({ type: 'ADD_AREA', area })
      } else {
        asyncUpdateArea(area)
      }
      return area
    },
    [state.owner, setCallingBackend, setError, asyncUpdateArea]
  )

  const deleteArea = useCallback(
    async area => {
      if (area) {
        if (area.id !== NO_AREA.id) {
          LOG.debug('Deleting area: ' + area.id)

          setCallingBackend(true)
          DataStore.delete(area)
            .catch(err => {
              setError('Failed to delete area: ' + err)
              throw err
            })
            .finally(() => setCallingBackend(false))

          dispatch({ type: 'REMOVE_AREA', area })
        } else {
          LOG.warn('Cannot delete', NO_AREA.name)
        }
      } else {
        LOG.warn('Cannot delete unspecified area')
      }
    },
    [setCallingBackend, setError]
  )

  const editArea = useCallback(
    async (id, name, colour) => {
      LOG.debug('Edit Area', id, name)

      if (id !== NO_AREA.id) {
        setCallingBackend(true)
        DataStore.save(
          Area.copyOf(getAreaById(id), updated => {
            updated.clientId = CLIENT_ID
            updated.name = encrypt(name)
            updated.colour = colour
          })
        )
          .then(area => dispatch({ type: 'UPDATE_AREA', area }))
          .catch(err => setError('Failed to edit area: ' + err))
          .finally(() => setCallingBackend(false))
      }
    },
    [getAreaById, setCallingBackend, setError]
  )

  const getAreaFilter = useCallback(() => {
    return state.areaFilter
  }, [state.areaFilter])

  const updateAreaFilter = useCallback(filter => {
    saveLocal(
      AREA_FILTER_LS_KEY,
      filter.map(area => area.id)
    )
    dispatch({ type: 'SET_AREA_FILTER', filter })
  }, [])

  // Other
  const triggerRepeats = useCallback(() => {
    LOG.debug('Trigger Repeats', state.repeats)

    if (isTriggeringRepeats || (!state.isOffline && !state.datastoreReady)) {
      LOG.debug(
        'Cannot trigger repeats: ',
        'Is Triggering Already: ' + isTriggeringRepeats,
        'Is Offline: ' + state.isOffline,
        'Datastore Ready: ' + state.datastoreReady
      )
      return
    }

    setTriggeringRepeats(true)

    const TODAY = moment().startOf('day')

    const isUncheckedRepeat = repeat => {
      return (
        repeat &&
        (!repeat.lastUpdate || moment(repeat.lastUpdate).isBefore(TODAY, 'day'))
      )
    }

    processLargeArrayAsync(
      'Check repeating TODOs',
      state.todos.filter(todo => todo.list === FOCUS.REPEATING.id),
      async repeatingTodo => {
        const repeat = getRepeatById(repeatingTodo.repeat)
        if (isUncheckedRepeat(repeat)) {
          const rule = new RepeatPattern(repeat.pattern)
          if (rule.isValidRepeatPattern()) {
            const nextDate = rule.nextDate(moment(repeat.startDate).toDate())
            LOG.debug('Next Date', nextDate)
            if (nextDate && moment(nextDate).isSame(TODAY, 'day')) {
              createFullTodo(
                decrypt(repeatingTodo.name),
                decrypt(repeatingTodo.description),
                null,
                repeatingTodo.tags,
                repeatingTodo.area,
                repeatingTodo.project,
                FOCUS.TODAY.id,
                null,
                repeat.id
              )
              await editRepeat(
                Repeat.copyOf(repeat, updated => {
                  updated.clientId = CLIENT_ID
                  updated.lastUpdate = TODAY.format('YYYY-MM-DD')
                })
              )
            }
          }
        }
      },
      processLargeArrayAsync(
        'Refresh all repeat start dates',
        state.todos.filter(todo => todo.list === FOCUS.REPEATING.id),
        async todo => {
          const repeat = getRepeatById(todo.repeat)
          if (repeat) {
            LOG.debug('Check Repeat', decrypt(todo.name), repeat.pattern)
            const rule = new RepeatPattern(repeat.pattern)
            if (rule) {
              const startDate = rule.nextDate(
                moment(repeat.startDate).toDate(),
                false
              )
              if (startDate) {
                LOG.debug(
                  'Next repeat time',
                  startDate,
                  moment(startDate).format('LL')
                )
                if (!moment(startDate).isSame(moment(todo.start))) {
                  await editTodoStart(todo.id, startDate)
                }
              } else {
                LOG.info(
                  'Delete Expired Repeat',
                  decrypt(todo.name),
                  repeat.pattern
                )
                deleteTodo(todo)
              }
            }
          }
        },
        () => setTriggeringRepeats(false),
        true
      ),
      true
    )

    LOG.debug('Finished Triggering Repeats')
  }, [
    createFullTodo,
    deleteTodo,
    editRepeat,
    editTodoStart,
    getRepeatById,
    setTriggeringRepeats,
    state.isOffline,
    state.repeats,
    state.datastoreReady,
    state.todos
  ])

  const housekeeping = useCallback(() => {
    if (isHousekeeping || (!state.isOffline && !state.datastoreReady)) {
      LOG.debug(
        'Cannot housekeep: ',
        'Is Housekeeping Already: ' + isHousekeeping,
        'Is Offline: ' + state.isOffline,
        'Datastore Ready: ' + state.datastoreReady
      )
      return
    }

    setHousekeeping(true)

    const COMPLETE_EXPIRED = moment()
      .startOf('day')
      .subtract(EXPIRY_INTERVAL_COMPLETE_DAYS, 'days')
    const TRASH_EXPIRED = moment()
      .startOf('day')
      .subtract(EXPIRY_INTERVAL_TRASH_DAYS, 'days')

    processLargeArrayAsync(
      'Check Trash for items to permanently delete',
      state.todos.filter(todo => todo.list === FOCUS.TRASH.id),
      todo => {
        LOG.debug(
          'Interval: Check todo for permanent deletion',
          todo,
          TRASH_EXPIRED
        )

        if (
          todo.list === FOCUS.TRASH.id &&
          todo.deleted &&
          moment(todo.deleted).isBefore(TRASH_EXPIRED)
        ) {
          deleteTodo(todo, false)
        }
      },
      processLargeArrayAsync(
        'Check completed TODOs for deletion',
        state.todos.filter(todo => todo.status === 'done'),
        todo => {
          LOG.debug('Interval: Check todo for deletion', todo, COMPLETE_EXPIRED)

          if (
            todo.list !== FOCUS.TRASH.id &&
            todo.status === 'done' &&
            todo.completed &&
            moment(todo.completed).isBefore(COMPLETE_EXPIRED)
          ) {
            moveTodoToTrash(todo, false)
          }
        },
        () => setHousekeeping(false)
      )
    )
  }, [
    deleteTodo,
    moveTodoToTrash,
    setHousekeeping,
    state.isOffline,
    state.datastoreReady,
    state.todos
  ])

  const deleteAll = useCallback(
    (useTrash = false, performDispatch = true) => {
      const allTodos = getTodos(null, true, true)
      if (useTrash) {
        processLargeArrayAsync('Move all TODOs to Trash', allTodos, todo =>
          moveTodoToTrash(todo, performDispatch)
        )
      } else {
        processLargeArrayAsync('Permanently delete all TODOs', allTodos, todo =>
          deleteTodo(todo, performDispatch)
        )
      }

      const allProjects = getProjects()
      processLargeArrayAsync(
        'Permanently delete all Projects',
        allProjects,
        project => deleteProject(project)
      )

      const allAreas = getAreas()
      processLargeArrayAsync('Permanently delete all Areas', allAreas, area =>
        deleteArea(area)
      )

      const allRepeats = getRepeats()
      processLargeArrayAsync(
        'Permanently delete all Repeats',
        allRepeats,
        repeat => deleteRepeat(repeat.id)
      )

      const allTags = getTags()
      processLargeArrayAsync('Permanently delete all Tags', allTags, tag =>
        deleteTag(tag)
      )
    },
    [
      deleteArea,
      deleteProject,
      deleteRepeat,
      deleteTag,
      deleteTodo,
      getAreas,
      getProjects,
      getRepeats,
      getTags,
      getTodos,
      moveTodoToTrash
    ]
  )

  // Subscribe to updates
  const handleSubscriptions = async () => {
    LOG.debug('Subscribe to TODO changes')
    observeTodos(CLIENT_ID, (todo, opType) => {
      switch (opType) {
        case 'UPDATE':
        case 'INSERT':
          asyncUpdateTodo(todo)
          break
        case 'DELETE':
          asyncDeleteTodo(todo)
          break
        default:
          break
      }
    })

    LOG.debug('Subscribe to Project changes')
    observeProjects(CLIENT_ID, (project, opType) => {
      switch (opType) {
        case 'UPDATE':
        case 'INSERT':
          asyncUpdateProject(project)
          break
        case 'DELETE':
          asyncDeleteProject(project)
          break
        default:
          break
      }
    })

    LOG.debug('Subscribe to Area changes')
    observeAreas(CLIENT_ID, (area, opType) => {
      switch (opType) {
        case 'UPDATE':
        case 'INSERT':
          asyncUpdateArea(area)
          break
        case 'DELETE':
          asyncDeleteArea(area)
          break
        default:
          break
      }
    })

    LOG.debug('Subscribe to Repeat changes')
    observeRepeats(CLIENT_ID, (repeat, opType) => {
      switch (opType) {
        case 'UPDATE':
        case 'INSERT':
          asyncUpdateRepeat(repeat)
          break
        case 'DELETE':
          asyncDeleteRepeat(repeat)
          break
        default:
          break
      }
    })

    LOG.debug('Subscribe to Tag changes')
    observeTags(CLIENT_ID, (tag, opType) => {
      switch (opType) {
        case 'UPDATE':
        case 'INSERT':
          asyncUpdateTag(tag)
          break
        case 'DELETE':
          asyncDeleteTag(tag)
          break
        default:
          break
      }
    })

    LOG.debug('Subscribe to Attachment changes')
    observeAttachments(CLIENT_ID, (tag, opType) => {
      switch (opType) {
        case 'UPDATE':
        case 'INSERT':
          asyncUpdateAttachment(tag)
          break
        case 'DELETE':
          asyncDeleteAttachment(tag)
          break
        default:
          break
      }
    })
  }

  ////////////////////////////////////////////////////////////////////
  // Inital load from backend and subscriptions
  useMountEffect(() => {
    async function loadData() {
      // This is for mobile views.
      // The AmplifyAuthenticator makes it scroll down
      window.scrollTo(0, 0)

      LOG.info('Loading data, please wait...')
      dispatch({ type: 'SET_LOADED', loaded: false })

      const { username } = await Auth.currentAuthenticatedUser()
      LOG.info('User login detected', username)
      dispatch({ type: 'SET_OWNER', owner: username })

      encryption.setPassword(username)

      const todos = await getAllTodos()
      dispatch({ type: 'SET_TODOS', todos })

      const projects = await getAllProjects()
      dispatch({ type: 'SET_PROJECTS', projects })

      const areas = await getAllAreas()
      dispatch({ type: 'SET_AREAS', areas })

      const tags = await getAllTags()
      dispatch({ type: 'SET_TAGS', tags })

      const attachments = await getAllAttachments()
      dispatch({ type: 'SET_ATTACHMENTS', attachments })

      const areaFilterSetting = getLocal(AREA_FILTER_LS_KEY)
      if (areaFilterSetting) {
        dispatch({
          type: 'SET_AREA_FILTER',
          filter: areas.filter(
            area => areaFilterSetting.indexOf(area.id) !== -1
          )
        })
      }
      const repeats = await getAllRepeats()
      dispatch({ type: 'SET_REPEATS', repeats })

      LOG.info('Setting up listeners...')
      handleSubscriptions()

      LOG.info('Finished loading')
      dispatch({ type: 'SET_LOADED', loaded: true })
    }
    loadData()

    Hub.listen('datastore', data => {
      LOG.debug('Datastore messages: ', data)
      dispatch({ type: 'SET_SYNC_STATUS', status: data.payload.event })
      switch (data.payload.event) {
        case 'networkStatus':
          LOG.debug('Network status', data.payload.data.active)
          dispatch({
            type: 'SET_IS_OFFLINE',
            isOffline: !data.payload.data.active
          })
          break
        case 'outboxStatus':
          LOG.debug('Outbox Status', data.payload.data.isEmpty)
          setSyncing(!data.payload.data.isEmpty)
          break
        default:
          LOG.debug(data.payload.event, data.payload.data)
          break
      }
    })

    Hub.listen('auth', data => {
      LOG.debug('Listening for all auth messages: ', data)
    })
  })

  useInterval(
    () => {
      triggerRepeats()
    },
    REPEAT_CHECK_INTERVAL_MS,
    state.datastoreReady
  )

  useInterval(
    () => {
      const COMPLETE_EXPIRED = moment()
        .startOf('day')
        .subtract(EXPIRY_INTERVAL_COMPLETE_DAYS, 'days')
      const TRASH_EXPIRED = moment()
        .startOf('day')
        .subtract(EXPIRY_INTERVAL_TRASH_DAYS, 'days')

      const emptyTrash = () => {
        LOG.debug('Interval: Empty Trash')

        const trash = getTodos(FOCUS.TRASH.id)

        LOG.debug('Interval: ' + trash.length + ' trash items')

        processLargeArrayAsync(
          'Check Trash for items to permanently delete',
          trash,
          todo => {
            LOG.debug(
              'Interval: Check todo for permanent deletion',
              todo,
              TRASH_EXPIRED
            )

            if (todo.deleted && moment(todo.deleted).isBefore(TRASH_EXPIRED)) {
              deleteTodo(todo, false)
            }
          }
        )

        LOG.debug('Interval: Finished emptying Trash')
      }

      const clearComplete = () => {
        LOG.debug('Interval: Clear Complete')

        const complete = getTodos().filter(todo => todo.status === 'done')

        LOG.debug('Interval: complete ' + complete.length + ' todos to check')

        processLargeArrayAsync(
          'Check completed TODOs for deletion',
          complete,
          todo => {
            LOG.debug(
              'Interval: Check todo for deletion',
              todo,
              COMPLETE_EXPIRED
            )

            if (
              todo.list !== FOCUS.TRASH.id &&
              todo.status === 'done' &&
              todo.completed &&
              moment(todo.completed).isBefore(COMPLETE_EXPIRED)
            ) {
              moveTodoToTrash(todo, false)
            }
          }
        )

        LOG.debug('Interval: Finished clearing Complete')
      }

      emptyTrash()
      clearComplete()
    },
    EXPIRY_CHECK_INTERVAL_MS,
    state.datastoreReady
  )

  const refreshForeignTodos = useCallback(() => {
    getAllForeignTodos(CLIENT_ID, todos => {
      LOG.debug('Refreshing foreign todos', todos.length)
      processLargeArrayAsync(
        'Refresh TODOs changed in other instances',
        todos,
        todo => {
          LOG.debug('Refreshing todo', decrypt(todo.name))
          dispatch({ type: 'UPDATE_TODO', todo })
        }
      )
    })
  }, [])

  useEffect(() => {
    const item = navigation.find(element =>
      pathToRegexp(element.path).test(location.pathname)
    )

    let selectedItem = item

    if (item && item.id === 'project') {
      selectedItem = JSON.parse(JSON.stringify(item))
      selectedItem.id = location.pathname.substring(9)
    }

    if (selectedItem) {
      if (!_.isEqual(selectedItem, state.navigationSelectedItem)) {
        dispatch({ type: 'SET_NAVIGATION_ITEM', item: selectedItem })

        switch (selectedItem.id) {
          case 'all':
          case 'today':
          case 'repeating':
            triggerRepeats()
            break
          default:
            LOG.debug('No action needed for page: ' + selectedItem.id)
            break
        }
      }
    }
  }, [location, triggerRepeats, state.navigationSelectedItem])

  useMountEffect(() => {
    history.listen((newLocation, action) => {
      LOG.debug(action, newLocation.pathname, newLocation.state)
      // Note if using this in future, that access to state is limited here
    })
  })

  ////////////////////////////////////////////////////////////////////
  // Misc
  const redirectToProject = useCallback(
    id => {
      LOG.debug('Redirecting to project', id)
      handleRedirect('/project/' + id)
    },
    [handleRedirect]
  )

  ////////////////////////////////////////////////////////////////////
  // State
  function reducer(reducerState, action) {
    LOG.debug('State Reducer', action.type, action)

    const isItemNewer = (itemIndex, itemsArray, newItem) => {
      // If version is undefined, then it's likely come from the local state management
      // If version is the same, assume it's an update, because we don't manage version locally
      const newer =
        newItem.clientId === CLIENT_ID || // If we updated it, ignore version, we always want the change
        itemsArray[itemIndex]._version === undefined ||
        newItem._version === undefined ||
        itemsArray[itemIndex]._version < newItem._version
      if (!newer) {
        LOG.debug(
          'Item is not the latest, ignoring update',
          itemsArray[itemIndex],
          newItem
        )
      }

      return newer
    }

    switch (action.type) {
      case 'SET_LOADED':
        return { ...reducerState, loaded: action.loaded }
      case 'SET_OWNER':
        return { ...reducerState, owner: action.owner }
      case 'SET_INFO':
        return { ...reducerState, info: action.info }
      case 'SET_WARNING':
        return { ...reducerState, warning: action.warning }
      case 'SET_ERROR':
        return { ...reducerState, error: action.error }
      case 'SET_SYNC_STATUS': {
        let datastoreReady = reducerState.datastoreReady
        if (action.status === 'ready') {
          datastoreReady = true
        }
        return {
          ...reducerState,
          syncStatus: action.status,
          datastoreReady: datastoreReady
        }
      }
      case 'SET_IS_CALLING_BACKEND':
        return { ...reducerState, isCallingBackend: action.isCallingBackend }
      case 'SET_IS_OFFLINE':
        return { ...reducerState, isOffline: action.isOffline }
      case 'SET_IS_SYNCING':
        return { ...reducerState, isSyncing: action.isSyncing }
      case 'SHOW_SUBSCRIPTION_PLANS':
        return { ...reducerState, showSubscriptionPlans: action.showPlans }
      case 'OPEN_AREA_FILTER':
        return { ...reducerState, openAreaFilter: action.showAreaFilter }
      case 'SET_AREA_FILTER':
        return { ...reducerState, areaFilter: action.filter }
      case 'SET_NAVIGATION_ITEM':
        return { ...reducerState, navigationSelectedItem: action.item }
      case 'CACHE_DRAG_LIST':
        return {
          ...reducerState,
          cacheDragList: {
            ...reducerState.cacheDragList,
            [action.list]: action.todos ? action.todos : []
          }
        }
      case 'CONFIRM_PROMOTE_TODO':
        return {
          ...reducerState,
          confirmAutoPromote: action.todo
            ? {
                todo: action.todo,
                position: action.position
              }
            : null
        }
      case 'CONFIRM_DELETE_PROJECT':
        return {
          ...reducerState,
          confirmAutoDelete: action.project
            ? {
                project: action.project
              }
            : null
        }

      // TODOs
      case 'SET_TODOS':
        countCache.clear()
        todoCache.clear()
        return { ...reducerState, todos: action.todos }
      case 'ADD_TODO':
        countCache.clear(action.todo.list)
        todoCache.clear(action.todo.list)
        return { ...reducerState, todos: [...reducerState.todos, action.todo] }
      case 'REMOVE_TODO': {
        const newTodos = [...reducerState.todos]
        const todoIndex = newTodos.findIndex(todo => todo.id === action.todo.id)
        if (todoIndex >= 0) {
          countCache.clear(action.todo.list)
          todoCache.clear(action.todo.list)
          newTodos.splice(todoIndex, 1)
          return { ...reducerState, todos: newTodos }
        } else {
          LOG.debug('Todo not found, assume deleted locally', action.todo.id)
        }
        break
      }
      case 'REMOVE_TODOS': {
        countCache.clear()
        todoCache.clear()
        const newTodos = [...reducerState.todos]
        action.todos.forEach(updatedTodo => {
          const todoIndex = newTodos.findIndex(
            todo => todo.id === updatedTodo.id
          )
          if (todoIndex >= 0) {
            newTodos.splice(todoIndex, 1)
          }
        })

        return { ...reducerState, todos: newTodos }
      }
      case 'UPDATE_TODO': {
        const newTodos = [...reducerState.todos]
        const todoIndex = newTodos.findIndex(todo => todo.id === action.todo.id)
        if (todoIndex >= 0) {
          if (isItemNewer(todoIndex, newTodos, action.todo)) {
            countCache.clear(action.todo.list)
            todoCache.clear(action.todo.list)
            const [removed] = newTodos.splice(todoIndex, 1, action.todo)
            countCache.clear(removed.list)
            todoCache.clear(removed.list)
            return { ...reducerState, todos: newTodos }
          }
        } else {
          // This happens when there's two instances running locally
          LOG.debug('Todo not found, adding...', action.todo.id)
          countCache.clear(action.todo.list)
          todoCache.clear(action.todo.list)
          newTodos.push(action.todo)
          return { ...reducerState, todos: newTodos }
        }
        break
      }
      case 'UPDATE_TODOS': {
        countCache.clear()
        todoCache.clear()
        const newTodos = [...reducerState.todos]
        action.todos.forEach(updatedTodo => {
          const todoIndex = newTodos.findIndex(
            todo => todo.id === updatedTodo.id
          )
          if (todoIndex >= 0) {
            if (isItemNewer(todoIndex, newTodos, updatedTodo)) {
              newTodos.splice(todoIndex, 1, updatedTodo)
            }
          } else {
            newTodos.splice(0, 0, updatedTodo)
          }
        })

        return { ...reducerState, todos: newTodos }
      }

      // Projects
      case 'SET_PROJECTS': {
        return { ...reducerState, projects: sortByName(action.projects) }
      }
      case 'ADD_PROJECT': {
        projectCache.clear()
        return {
          ...reducerState,
          projects: sortByName([...reducerState.projects, action.project])
        }
      }
      case 'REMOVE_PROJECT': {
        const newProjects = [...reducerState.projects]
        const projectIndex = newProjects.findIndex(
          project => project.id === action.project.id
        )
        if (projectIndex >= 0) {
          projectCache.clear()
          newProjects.splice(projectIndex, 1)
          return { ...reducerState, projects: newProjects }
        } else {
          LOG.debug(
            'Project not found, assume deleted locally',
            action.project.id
          )
        }
        break
      }
      case 'REMOVE_PROJECTS': {
        projectCache.clear()
        const newProjects = [...reducerState.projects]
        action.projects.forEach(updatedProject => {
          const projectIndex = newProjects.findIndex(
            project => project.id === updatedProject.id
          )
          if (projectIndex >= 0) {
            newProjects.splice(projectIndex, 1)
          }
        })

        return { ...reducerState, projects: newProjects }
      }

      case 'UPDATE_PROJECT': {
        projectCache.clear()
        const newProjects = [...reducerState.projects]
        const projectIndex = newProjects.findIndex(
          project => project.id === action.project.id
        )
        if (projectIndex >= 0) {
          if (isItemNewer(projectIndex, newProjects, action.project)) {
            newProjects.splice(projectIndex, 1, action.project)
          }
        } else {
          // This happens when there's two instances running locally
          LOG.debug('Project not found, adding...', action.project.id)
          newProjects.push(action.project)
        }
        return { ...reducerState, projects: newProjects }
      }
      case 'UPDATE_PROJECTS': {
        projectCache.clear()
        const newProjects = [...reducerState.projects]
        action.projects.forEach(updatedProject => {
          const projectIndex = newProjects.findIndex(
            project => project.id === updatedProject.id
          )
          if (projectIndex >= 0) {
            if (isItemNewer(projectIndex, newProjects, updatedProject)) {
              newProjects.splice(projectIndex, 1, updatedProject)
            }
          } else {
            newProjects.splice(0, 0, updatedProject)
          }
        })

        return { ...reducerState, projects: newProjects }
      }

      // Areas
      case 'SET_AREAS': {
        areaCache.clear()
        return { ...reducerState, areas: sortByName(action.areas) }
      }
      case 'ADD_AREA': {
        areaCache.clear()
        return {
          ...reducerState,
          areas: sortByName([...reducerState.areas, action.area])
        }
      }
      case 'REMOVE_AREA': {
        const newAreas = [...reducerState.areas]
        const areaIndex = newAreas.findIndex(area => area.id === action.area.id)
        if (areaIndex >= 0) {
          areaCache.clear()
          todoCache.clear()
          countCache.clear()
          newAreas.splice(areaIndex, 1)
          return { ...reducerState, areas: newAreas }
        } else {
          LOG.debug('Area not found, assume deleted locally', action.area.id)
        }
        break
      }
      case 'REMOVE_AREAS': {
        areaCache.clear()
        const newAreas = [...reducerState.areas]
        action.areas.forEach(updatedArea => {
          const areaIndex = newAreas.findIndex(
            area => area.id === updatedArea.id
          )
          if (areaIndex >= 0) {
            newAreas.splice(areaIndex, 1)
          }
        })

        return { ...reducerState, areas: newAreas }
      }
      case 'UPDATE_AREA': {
        areaCache.clear()
        const newAreas = [...reducerState.areas]
        const areaIndex = newAreas.findIndex(area => area.id === action.area.id)
        if (areaIndex >= 0) {
          if (isItemNewer(areaIndex, newAreas, action.area)) {
            newAreas.splice(areaIndex, 1, action.area)
          }
        } else {
          // This happens when there's two instances running locally
          LOG.debug('Area not found, adding...', action.area.id)
          newAreas.push(action.area)
        }
        return { ...reducerState, areas: newAreas }
      }
      case 'UPDATE_AREAS': {
        areaCache.clear()
        const newAreas = [...reducerState.areas]
        action.areas.forEach(updatedArea => {
          const areaIndex = newAreas.findIndex(
            area => area.id === updatedArea.id
          )
          if (areaIndex >= 0) {
            if (isItemNewer(areaIndex, newAreas, updatedArea)) {
              newAreas.splice(areaIndex, 1, updatedArea)
            }
          } else {
            newAreas.splice(0, 0, updatedArea)
          }
        })

        return { ...reducerState, areas: newAreas }
      }

      // Repeats
      case 'SET_REPEATS': {
        return { ...reducerState, repeats: action.repeats }
      }
      case 'ADD_REPEAT': {
        repeatCache.clear()
        return {
          ...reducerState,
          repeats: sortByName([...reducerState.repeats, action.repeat])
        }
      }
      case 'REMOVE_REPEAT': {
        const newRepeats = [...reducerState.repeats]
        const repeatIndex = newRepeats.findIndex(
          repeat => repeat.id === action.repeat.id
        )
        if (repeatIndex >= 0) {
          repeatCache.clear()
          newRepeats.splice(repeatIndex, 1)
          return { ...reducerState, repeats: newRepeats }
        } else {
          LOG.debug(
            'Repeat not found, assume deleted locally',
            action.repeat.id
          )
        }
        break
      }
      case 'REMOVE_REPEATS': {
        repeatCache.clear()
        const newRepeats = [...reducerState.repeats]
        action.repeats.forEach(updatedRepeat => {
          const repeatIndex = newRepeats.findIndex(
            repeat => repeat.id === updatedRepeat.id
          )
          if (repeatIndex >= 0) {
            newRepeats.splice(repeatIndex, 1)
          }
        })

        return { ...reducerState, repeats: newRepeats }
      }
      case 'UPDATE_REPEAT': {
        repeatCache.clear()
        const newRepeats = [...reducerState.repeats]
        const repeatIndex = newRepeats.findIndex(
          repeat => repeat.id === action.repeat.id
        )
        if (repeatIndex >= 0) {
          if (isItemNewer(repeatIndex, newRepeats, action.repeat)) {
            newRepeats.splice(repeatIndex, 1, action.repeat)
          }
        } else {
          // This happens when there's two instances running locally
          LOG.debug('Repeat not found, adding...', action.repeat.id)
          newRepeats.push(action.repeat)
        }
        return { ...reducerState, repeats: newRepeats }
      }
      case 'UPDATE_REPEATS': {
        repeatCache.clear()
        const newRepeats = [...reducerState.repeats]
        action.repeats.forEach(updatedRepeat => {
          const repeatIndex = newRepeats.findIndex(
            repeat => repeat.id === updatedRepeat.id
          )
          if (repeatIndex >= 0) {
            if (isItemNewer(repeatIndex, newRepeats, updatedRepeat)) {
              newRepeats.splice(repeatIndex, 1, updatedRepeat)
            }
          } else {
            newRepeats.splice(0, 0, updatedRepeat)
          }
        })

        return { ...reducerState, repeats: newRepeats }
      }

      // Tags
      case 'SET_TAGS':
        return { ...reducerState, tags: action.tags }
      case 'ADD_TAG': {
        tagCache.clear()
        return {
          ...reducerState,
          tags: sortByName([...reducerState.tags, action.tag])
        }
      }
      case 'REMOVE_TAG': {
        const newTags = [...reducerState.tags]
        const tagIndex = newTags.findIndex(tag => tag.id === action.tag.id)
        if (tagIndex >= 0) {
          tagCache.clear()
          newTags.splice(tagIndex, 1)
          return { ...reducerState, tags: newTags }
        } else {
          LOG.debug('Tag not found, assume deleted locally', action.tag.id)
        }
        break
      }
      case 'REMOVE_TAGS': {
        tagCache.clear()
        const newTags = [...reducerState.tags]
        action.tags.forEach(updatedTag => {
          const tagIndex = newTags.findIndex(tag => tag.id === updatedTag.id)
          if (tagIndex >= 0) {
            newTags.splice(tagIndex, 1)
          }
        })

        return { ...reducerState, tags: newTags }
      }
      case 'UPDATE_TAGS': {
        tagCache.clear()
        const newTags = [...reducerState.tags]
        action.tags.forEach(updatedTag => {
          const tagIndex = newTags.findIndex(tag => tag.id === updatedTag.id)
          if (tagIndex >= 0) {
            if (isItemNewer(tagIndex, newTags, updatedTag)) {
              newTags.splice(tagIndex, 1, updatedTag)
            }
          } else {
            newTags.splice(0, 0, updatedTag)
          }
        })

        return { ...reducerState, tags: newTags }
      }

      // Attachments
      case 'SET_ATTACHMENTS': {
        let currentAttachmentsSize = 0
        if (action.attachments) {
          action.attachments.forEach(attachment => {
            currentAttachmentsSize += attachment.size
          })
        }
        LOG.debug('Current Storage usage', currentAttachmentsSize)
        return {
          ...reducerState,
          attachments: action.attachments,
          attachmentsSize: currentAttachmentsSize
        }
      }
      case 'ADD_ATTACHMENT': {
        attachmentCache.clear()
        LOG.debug(
          'Current Storage usage',
          reducerState.attachmentsSize + action.attachment.size
        )

        return {
          ...reducerState,
          attachments: sortByName([
            ...reducerState.attachments,
            action.attachment
          ]),
          attachmentsSize: reducerState.attachmentsSize + action.attachment.size
        }
      }
      case 'REMOVE_ATTACHMENT': {
        const newAttachments = [...reducerState.attachments]
        const attachmentIndex = newAttachments.findIndex(
          attachment => attachment.id === action.attachment.id
        )
        if (attachmentIndex >= 0) {
          attachmentCache.clear()
          newAttachments.splice(attachmentIndex, 1)
          LOG.debug(
            'Current Storage usage',
            reducerState.attachmentsSize - action.attachment.size
          )

          return {
            ...reducerState,
            attachments: newAttachments,
            attachmentsSize:
              reducerState.attachmentsSize - action.attachment.size
          }
        } else {
          LOG.debug(
            'Attachment not found, assume deleted locally',
            action.attachment.id
          )
        }
        break
      }
      case 'REMOVE_ATTACHMENTS': {
        attachmentCache.clear()
        const newAttachments = [...reducerState.attachments]
        let removedSize = 0
        action.attachments.forEach(updatedAttachment => {
          const attachmentIndex = newAttachments.findIndex(
            attachment => attachment.id === updatedAttachment.id
          )
          if (attachmentIndex >= 0) {
            newAttachments.splice(attachmentIndex, 1)
          }
          removedSize += updatedAttachment.size
        })

        LOG.debug(
          'Current Storage usage',
          reducerState.attachmentsSize - removedSize
        )

        return {
          ...reducerState,
          attachments: newAttachments,
          attachmentsSize: reducerState.attachmentsSize - removedSize
        }
      }
      case 'UPDATE_ATTACHMENT': {
        attachmentCache.clear()
        const newAttachments = [...reducerState.attachments]
        const attachmentIndex = newAttachments.findIndex(
          attachment => attachment.id === action.attachment.id
        )
        if (attachmentIndex >= 0) {
          if (isItemNewer(attachmentIndex, newAttachments, action.attachment)) {
            newAttachments.splice(attachmentIndex, 1, action.attachment)
          }
        } else {
          // This happens when there's two instances running locally
          LOG.debug('Attachment not found, adding...', action.attachment.id)
          newAttachments.push(action.attachment)
        }
        return { ...reducerState, attachments: newAttachments }
      }

      case 'UPDATE_ATTACHMENTS': {
        attachmentCache.clear()
        const newAttachments = [...reducerState.attachments]
        let adjustSize = 0
        action.attachments.forEach(updatedAttachment => {
          const attachmentIndex = newAttachments.findIndex(
            attachment => attachment.id === updatedAttachment.id
          )
          if (attachmentIndex >= 0) {
            if (
              isItemNewer(attachmentIndex, newAttachments, updatedAttachment)
            ) {
              const [removed] = newAttachments.splice(
                attachmentIndex,
                1,
                updatedAttachment
              )
              adjustSize += updatedAttachment.size
              adjustSize -= removed.size
            }
          } else {
            newAttachments.splice(0, 0, updatedAttachment)
            adjustSize += updatedAttachment.size
          }
        })

        LOG.debug(
          'Current Storage usage',
          reducerState.attachmentsSize + adjustSize
        )

        return {
          ...reducerState,
          attachments: newAttachments,
          attachmentsSize: reducerState.attachmentsSize + adjustSize
        }
      }

      default:
        LOG.warn('Unrecognised action', action.type)
    }
    return reducerState
  }

  const whenVisibleUpdate = useCallback(() => {
    refreshForeignTodos()
    triggerRepeats()
  }, [refreshForeignTodos, triggerRepeats])

  const whenIdleUpdate = useCallback(() => {
    housekeeping()
  }, [housekeeping])

  const enforceLimitCreateProject = useCallback(
    (name, description, area, autoPromote, autoDelete, performDispatch) => {
      return enforceLimit(
        ITEM_TYPE.PROJECT,
        getUnfilteredProjects,
        setShowSubscriptionPlans,
        () =>
          createProject(
            name,
            description,
            area,
            autoPromote,
            autoDelete,
            performDispatch
          ),
        null,
        state.isOffline
      )
    },
    [
      createProject,
      getUnfilteredProjects,
      setShowSubscriptionPlans,
      state.isOffline
    ]
  )

  const enforceLimitCreateArea = useCallback(
    (name, colour, performDispatch) => {
      return enforceLimit(
        ITEM_TYPE.AREA,
        () => getAreas(false),
        setShowSubscriptionPlans,
        () => createArea(name, colour, performDispatch),
        setOpenAreaFilter,
        state.isOffline
      )
    },
    [
      createArea,
      getAreas,
      setOpenAreaFilter,
      setShowSubscriptionPlans,
      state.isOffline
    ]
  )

  const enforceLimitCreateTodo = useCallback(
    (newTodo, list, project, addTop) => {
      return enforceLimit(
        ITEM_TYPE.TODO,
        getUnfilteredTodos,
        setShowSubscriptionPlans,
        () => createTodo(newTodo, list, project, addTop),
        null,
        state.isOffline
      )
    },
    [createTodo, getUnfilteredTodos, setShowSubscriptionPlans, state.isOffline]
  )

  const enforceLimitCreateAttachment = useCallback(
    (
      todo,
      file,
      progressCallback = () => {},
      completeCallback = () => {},
      performDispatch = true
    ) => {
      return enforceLimit(
        ITEM_TYPE.ATTACHMENT,
        getUnfilteredAttachments,
        showPlans => {
          completeCallback()
          setShowSubscriptionPlans(showPlans)
        },
        () =>
          createAttachment(
            todo,
            file,
            progressCallback,
            completeCallback,
            performDispatch
          ),
        null,
        state.isOffline
      )
    },
    [
      createAttachment,
      getUnfilteredAttachments,
      setShowSubscriptionPlans,
      state.isOffline
    ]
  )

  return (
    <IdleRefresh
      whenVisibleCallback={whenVisibleUpdate}
      whenIdleCallback={whenIdleUpdate}
      triggerWhenVisibleAfterIdleMins={10}
      triggerAfterIdleMins={2}
    >
      <Fragment>
        {state.loaded && (
          <DragDropContext
            onDragEnd={result =>
              onDragEnd(editTodoProject, handleTodoDragEnd, result)
            }
          >
            <Navigation
              version={getVersion()}
              navigation={navigation}
              navigationSelectedItem={state.navigationSelectedItem}
              handleRedirect={handleRedirect}
              handleSelect={handleSelect}
              isCallingBackend={state.isCallingBackend}
              isSyncing={state.isSyncing}
              isOffline={state.isOffline}
              decrypt={decrypt}
              getOutstandingTodoListCount={getOutstandingTodoListCount}
              getUnfilteredProjects={getUnfilteredProjects}
              getProjects={getProjects}
              deleteProject={deleteProject}
              deleteAll={deleteAll}
              getExportableArea={getExportableArea}
              getExportableProject={getExportableProject}
              getExportableRepeat={getExportableRepeat}
              getExportableTag={getExportableTag}
              getExportableTodo={getExportableTodo}
              getAreas={getAreas}
              getAvailableTags={getAvailableTags}
              getUnfilteredTodos={getUnfilteredTodos}
              getTodos={getTodos}
              createFullTodo={createFullTodo}
              createProject={enforceLimitCreateProject}
              createRepeat={createRepeat}
              createTag={createTag}
              createArea={enforceLimitCreateArea}
              getAreaFilter={getAreaFilter}
              updateAreaFilter={updateAreaFilter}
              getAreaById={getAreaById}
              editArea={editArea}
              deleteArea={deleteArea}
              getRepeats={getRepeats}
              getTags={getTags}
              deleteTodo={deleteTodo}
              moveTodoToTrash={moveTodoToTrash}
              showSubscriptionPlans={state.showSubscriptionPlans}
              setShowSubscriptionPlans={setShowSubscriptionPlans}
              openAreaFilter={state.openAreaFilter}
              setOpenAreaFilter={setOpenAreaFilter}
              setInfo={setInfo}
              setWarning={setWarning}
              setError={setError}
              logout={logout}
            />
            <Main
              navigation={navigation}
              getUnfilteredTodos={getUnfilteredTodos}
              getTodos={getTodos}
              decrypt={decrypt}
              editTodoStatus={editTodoStatus}
              getRepeatPattern={getRepeatPattern}
              getProjectNameForTodo={getProjectNameForTodo}
              redirectToProject={redirectToProject}
              getProjectsForAreas={getProjectsForAreas}
              clientId={CLIENT_ID}
              createProject={createProject}
              deleteTodo={deleteTodo}
              getProjectById={getProjectById}
              editTodo={editTodo}
              getAvailableTags={getAvailableTags}
              createTodo={enforceLimitCreateTodo}
              editProject={editProject}
              getAreas={getAreas}
              getAreaById={getAreaById}
              deleteProject={deleteProject}
              handleTodoDragEnd={handleTodoDragEnd}
              moveTodoToTrash={moveTodoToTrash}
              cacheDragList={cacheDragList}
              getTagsFromTodo={getTagsFromTodo}
              createAttachment={enforceLimitCreateAttachment}
              deleteAttachment={deleteAttachment}
              downloadAttachment={downloadAttachment}
              editAttachment={editAttachment}
              getTodoAttachments={getTodoAttachments}
              setInfo={setInfo}
              setWarning={setWarning}
              setError={setError}
            />
            <Footer />
          </DragDropContext>
        )}
        {!state.loaded && <Splash />}
        <Popup
          open={state.info !== null}
          variant='success'
          onClose={useCallback(() => setInfo(null), [setInfo])}
          onCancel={useCallback(() => setInfo(null), [setInfo])}
          message={state.info}
        />
        <Popup
          open={state.warning !== null}
          variant='warning'
          onClose={useCallback(() => setWarning(null), [setWarning])}
          onCancel={useCallback(() => setWarning(null), [setWarning])}
          message={state.warning}
          autoHideDurationOverride={6000}
        />
        <Popup
          open={state.error !== null}
          variant='error'
          onClose={useCallback(() => setError(null), [setError])}
          onCancel={useCallback(() => setError(null), [setError])}
          message={state.error}
        />
        <ConfirmYesNoView
          open={state.confirmAutoPromote !== null}
          title={'Confirm Move Next Action'}
          message={useMemo(() => {
            return (
              <Typography component='span'>
                Do you want to move the next project action{' '}
                <em>
                  <b>
                    {state.confirmAutoPromote
                      ? decrypt(state.confirmAutoPromote.todo.name)
                      : ''}
                  </b>
                </em>{' '}
                to your{' '}
                <em>
                  <b>{FOCUS.TODAY.title}</b>
                </em>{' '}
                list?
              </Typography>
            )
          }, [state.confirmAutoPromote])}
          viewLabel='View Project'
          onYes={useCallback(() => {
            moveTodoToList(
              state.confirmAutoPromote.todo,
              FOCUS.TODAY.id,
              state.confirmAutoPromote.position
            )
            confirmPromoteTodo(null)
          }, [confirmPromoteTodo, moveTodoToList, state.confirmAutoPromote])}
          onNo={useCallback(
            () => confirmPromoteTodo(null, 0),
            [confirmPromoteTodo]
          )}
          onView={useCallback(() => {
            redirectToProject(state.confirmAutoPromote.todo.project)
            confirmPromoteTodo(null)
          }, [confirmPromoteTodo, redirectToProject, state.confirmAutoPromote])}
        />
        <ConfirmYesNoView
          open={state.confirmAutoDelete !== null}
          title={'Confirm Delete Project'}
          message={useMemo(() => {
            return (
              <Typography component='span'>
                Project{' '}
                <em>
                  <b>
                    {state.confirmAutoDelete
                      ? decrypt(state.confirmAutoDelete.project.name)
                      : ''}
                  </b>
                </em>{' '}
                is complete. Delete?
              </Typography>
            )
          }, [state.confirmAutoDelete])}
          viewLabel='View Project'
          onYes={useCallback(() => {
            deleteProject(state.confirmAutoDelete.project)
            confirmDeleteProject(null)
          }, [confirmDeleteProject, deleteProject, state.confirmAutoDelete])}
          onNo={useCallback(
            () => confirmDeleteProject(null, 0),
            [confirmDeleteProject]
          )}
          onView={useCallback(() => {
            redirectToProject(state.confirmAutoDelete.project.id)
            confirmDeleteProject(null)
          }, [
            confirmDeleteProject,
            redirectToProject,
            state.confirmAutoDelete
          ])}
        />
      </Fragment>
    </IdleRefresh>
  )
})

export default GettingFocusedApp
